import * as React from "react"
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface ComboboxOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  isLoading?: boolean
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select option...",
  emptyText = "No option found.",
  disabled = false,
  className,
  isLoading = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [highlightedIndex, setHighlightedIndex] = React.useState<number | null>(null)

  const selectedOption = options.find((option) => option.value === value)

  const handleSelect = React.useCallback((optionValue: string) => {
    onValueChange?.(optionValue)
    setOpen(false)
    setHighlightedIndex(null)
  }, [onValueChange])

  const handleClear = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onValueChange?.('')
    setOpen(false)
  }, [onValueChange])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled || isLoading}
        >
          <span className="truncate flex-1 text-left">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
                Loading...
              </>
            ) : selectedOption ? (
              selectedOption.label
            ) : (
              placeholder
            )}
          </span>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {!isLoading && value && (
              <button
                type="button"
                onClick={handleClear}
                className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin opacity-50" />
            ) : (
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[--radix-popover-trigger-width] p-0" 
        align="start"
        onInteractOutside={(e) => {
          // Allow interactions inside the popover
          const target = e.target as HTMLElement
          if (target.closest('[cmdk-list]') || target.closest('[cmdk-item]')) {
            e.preventDefault()
          }
        }}
      >
        <Command shouldFilter>
          <CommandInput placeholder="Search..." />
          <CommandList>
            {isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
                Loading...
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyText}</CommandEmpty>
                <CommandGroup>
              {options.map((option, index) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  disabled={option.disabled}
                  onSelect={(currentValue) => {
                    // Handle keyboard selection
                    const selectedOption = options.find(
                      (opt) => opt.label === currentValue
                    )
                    if (selectedOption) {
                      handleSelect(selectedOption.value)
                    }
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(index)
                  }}
                  onMouseLeave={() => {
                    setHighlightedIndex(null)
                  }}
                  className={cn(
                    highlightedIndex === index && "bg-accent text-accent-foreground"
                  )}
                >
                  <div className="mr-2 h-4 w-4 shrink-0 flex items-center justify-center">
                    <Check
                      className={cn(
                        "h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className={cn(
                      "font-semibold",
                      option.description && "text-sm"
                    )}>
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="text-xs text-muted-foreground mt-0.5">
                        {option.description}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
